import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Check,
  Star,
  CreditCard,
  Palette,
  Building2,
  Globe,
  UserPlus,
  Settings,
  Share2,
  TrendingUp,
  Shield,
  Truck,
  ThumbsUp,
  ChevronDown,
  Sparkles,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const productIcons = { brand: CreditCard, custom: Palette, enterprise: Building2, haiti_mondial: Globe };
const accentGradients = {
  brand: 'from-[#0A2540] to-[#0A2540]/80',
  custom: 'from-violet-600 to-purple-500',
  enterprise: 'from-amber-500 to-orange-500',
  haiti_mondial: 'from-emerald-600 to-teal-500'
};

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group" data-testid="faq-item">
        <span className="font-medium text-slate-900 pr-4 group-hover:text-[#0A2540] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-40 pb-5' : 'max-h-0'}`}>
        <p className="text-slate-500 text-sm leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetch(`${API}/products`).then(r => r.json()).then(setProducts).catch(() => {});
  }, []);

  const valueItems = t('value.items', { returnObjects: true });
  const valueIcons = [Sparkles, Share2, CreditCard, Settings, TrendingUp];

  return (
    <div className="bg-white">
      {/* ───── HERO ───── */}
      <section className="relative overflow-hidden pt-28 pb-24 sm:pt-36 sm:pb-32">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#0A2540]/10/50 rounded-full blur-[120px] -z-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <motion.div
              initial="hidden" animate="visible" variants={stagger}
              className="text-center lg:text-left flex-1"
            >
              <motion.h1 variants={fadeUp} transition={{ duration: 0.6 }}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-6"
                data-testid="hero-title"
              >
                {t('hero.title')}
              </motion.h1>
              <motion.p variants={fadeUp} transition={{ duration: 0.6, delay: 0.15 }}
                className="text-lg sm:text-xl text-slate-500 leading-relaxed mb-10 max-w-2xl lg:max-w-none"
              >
                {t('hero.subtitle')}
              </motion.p>
              <motion.div variants={fadeUp} transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Button
                  onClick={() => navigate('/products')}
                  className="h-14 px-8 text-base rounded-lg bg-[#00C48C] text-white shadow-lg shadow-[#00C48C]/25 hover:bg-[#00A67E] hover:shadow-[#00C48C]/40 hover:scale-[1.02] transition-all"
                  data-testid="hero-cta"
                >
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/how-it-works')}
                  className="h-14 px-8 text-base rounded-full border-2 border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="hero-cta-secondary"
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex-shrink-0"
              data-testid="hero-image"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_505eff31-e603-4e70-a514-ffac19360da5/artifacts/gjftep4m_Sans_titre-removebg-preview%20%281%29.png"
                alt="IKATone Digital Card Preview"
                className="w-[280px] sm:w-[320px] lg:w-[380px] h-auto drop-shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───── VALUE PROPOSITION ───── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('value.title')}
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5"
          >
            {Array.isArray(valueItems) && valueItems.map((item, i) => {
              const Icon = valueIcons[i] || Check;
              return (
                <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl p-6 text-center border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-[#0A2540]/5 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-[#0A2540]" />
                  </div>
                  <p className="text-sm font-medium text-slate-700 leading-snug">{item}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="py-24" id="how-it-works">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('howItWorks.title')}
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid md:grid-cols-4 gap-8"
          >
            {[
              { icon: CreditCard, num: '01', title: t('howItWorks.step1.title'), desc: t('howItWorks.step1.d1') },
              { icon: UserPlus, num: '02', title: t('howItWorks.step2.title'), desc: t('howItWorks.step2.d1') },
              { icon: Share2, num: '03', title: t('howItWorks.step3.title'), desc: t('howItWorks.step3.d1') },
              { icon: TrendingUp, num: '04', title: t('landing.showcase.title', '').split(' ')[0] || 'Grow', desc: t('howItWorks.step3.d4') }
            ].map((step, i) => (
              <motion.div key={i} variants={fadeUp} className="relative text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#0A2540] text-white flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#0A2540]/20">
                  <step.icon className="w-7 h-7" />
                </div>
                <span className="absolute -top-2 -right-2 md:right-auto md:left-[calc(50%+24px)] text-5xl font-bold text-slate-100 select-none">{step.num}</span>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ───── PRODUCT SHOWCASE ───── */}
      <section className="py-24 bg-slate-50" id="products-showcase">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              {t('landing.showcase.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-500 max-w-xl mx-auto">
              {t('landing.showcase.subtitle')}
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {products.map((p, i) => {
              const pt = p.product_id.replace('card_', '');
              const Icon = productIcons[pt] || CreditCard;
              const grad = accentGradients[pt] || accentGradients.brand;
              const td = t(`productData.${pt}`, { returnObjects: true });
              const name = td?.name || p.name;
              const desc = td?.shortDescription || p.short_description || p.description;
              return (
                <motion.div key={p.product_id} variants={fadeUp}
                  className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`showcase-${pt}`}
                >
                  <div className={`h-1.5 bg-gradient-to-r ${grad}`} />
                  <div className="p-6">
                    <div className="flex justify-center mb-5">
                      <img src={p.image} alt={name} className="w-28 h-28 object-contain group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${grad} flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-heading text-base font-bold text-slate-900">{name}</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-5 leading-relaxed line-clamp-2">{desc}</p>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/products/${pt}`)}
                      className={`w-full rounded-full border-slate-200 hover:bg-gradient-to-r hover:${grad} hover:text-white hover:border-transparent transition-all`}
                    >
                      {t('landing.showcase.viewDetails')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ───── POSITIONING ───── */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A2540]/5 border border-[#E5E7EB] text-sm text-[#0A2540] mb-6"
            >
              <Zap className="w-4 h-4" />
              IKATone
            </motion.div>
            <motion.h2 variants={fadeUp}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-5"
            >
              {t('landing.positioning')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-slate-500 max-w-2xl mx-auto mb-10">
              {t('landing.positioningDesc')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Button
                onClick={() => navigate('/products')}
                className="h-14 px-8 text-base rounded-lg bg-[#00C48C] text-white shadow-lg shadow-[#00C48C]/25 hover:bg-[#00A67E] hover:shadow-[#00C48C]/40 hover:scale-[1.02] transition-all"
              >
                {t('hero.cta')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ───── SOCIAL PROOF ───── */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.socialProof.title')}
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { text: t('landing.socialProof.review1'), author: t('landing.socialProof.review1Author') },
              { text: t('landing.socialProof.review2'), author: t('landing.socialProof.review2Author') },
              { text: t('landing.socialProof.review3'), author: t('landing.socialProof.review3Author') }
            ].map((review, i) => (
              <motion.div key={i} variants={fadeUp}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm"
              >
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed mb-5 italic">"{review.text}"</p>
                <p className="text-sm font-semibold text-slate-900">{review.author}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ───── TRUST / SECURITY ───── */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              {t('landing.trust.title')}
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid sm:grid-cols-3 gap-8"
          >
            {[
              { icon: Shield, title: t('landing.trust.securePayment'), desc: t('landing.trust.securePaymentDesc') },
              { icon: Truck, title: t('landing.trust.fastDelivery'), desc: t('landing.trust.fastDeliveryDesc') },
              { icon: ThumbsUp, title: t('landing.trust.satisfaction'), desc: t('landing.trust.satisfactionDesc') }
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-heading text-base font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section className="py-24 bg-slate-50" id="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              {t('landing.faq.title')}
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y-0 px-8"
            data-testid="faq-section"
          >
            {[1, 2, 3, 4, 5, 6].map(i => (
              <FaqItem key={i} q={t(`landing.faq.q${i}`)} a={t(`landing.faq.a${i}`)} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-5"
            >
              {t('landing.finalCta.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-slate-500 max-w-xl mx-auto mb-10">
              {t('landing.finalCta.subtitle')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Button
                onClick={() => navigate('/products')}
                className="h-14 px-10 text-lg rounded-lg bg-[#00C48C] text-white shadow-lg shadow-[#00C48C]/25 hover:bg-[#00A67E] hover:shadow-[#00C48C]/40 hover:scale-[1.02] transition-all"
                data-testid="final-cta"
              >
                {t('landing.finalCta.cta')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
